import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget, CompactInlineAlertWidget, InlineAlertType } from '@tableau/widgets';
import React, { useState } from 'react';

import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin } from '~src/settings/types/ISiteSettingsForSiteAdmin';

import { IUpdateSiteSettingsForSiteAdminParams, UpdateSiteSettingsForSiteAdminRequest } from '../../ServerApi/requests/siteSettings/UpdateSiteSettingsForSiteAdminRequest';

export interface ScimExternalIdToggleProps {
    siteSettingsForSiteAdmin: ISiteSettingsForSiteAdmin;
}

export function ScimExternalIdToggle({ siteSettingsForSiteAdmin }: ScimExternalIdToggleProps) {
    const [saved, setSaved] = useState<boolean>(SessionSiteSelectors.isScimAttributesEnabled(getAppStore().getState()));
    const [draft, setDraft] = useState<boolean>(saved);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    if (!siteSettingsForSiteAdmin) {
        return null;
    }

    const hasChanges = draft !== saved;
    const showWarning = hasChanges && !draft && saved;

    const handleChange = () => {
        setDraft(!draft);
    };

    const handleCancel = () => {
        setDraft(saved);
    };

    const handleSave = async () => {
        setIsSaving(true);
        const siteAdminSettings = siteSettingsForSiteAdmin.siteAdminSettings as ISiteAdminSettings;
        try {
            siteAdminSettings.siteSettingsV2 = { ...siteAdminSettings.siteSettingsV2, scimAttributesEnabled: draft };
            const params: IUpdateSiteSettingsForSiteAdminParams = {
                id: (siteSettingsForSiteAdmin as any).id,
                siteAdminSettings,
                subscriptionSettings: siteSettingsForSiteAdmin.subscriptionSettings
            };
            await WebHttpClient.sendRequest(new UpdateSiteSettingsForSiteAdminRequest(params));
            setSaved(draft);
            getSessionManagementServiceInstance().refreshSessionInfo();
            ToasterService.instance.pop({
                type: ToastType.Success,
                text: draft ? Messages.scimExternalIdEnabledSuccess() : Messages.scimExternalIdDisabledSuccess()
            });
        } catch {
            siteAdminSettings.siteSettingsV2 = { ...siteAdminSettings.siteSettingsV2, scimAttributesEnabled: saved };
            ToasterService.instance.pop({
                type: ToastType.Error,
                text: draft ? Messages.scimExternalIdEnableError() : Messages.scimExternalIdDisableError()
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div>
            <CheckBoxWithLabelWidget
                testId='scim-external-id-toggle'
                label={Messages.scimExternalIdSettingLabel()}
                checked={draft}
                handleChange={handleChange}
                disabled={isSaving}
                removeLeftMargin={true}
            />
            {showWarning &&
                <CompactInlineAlertWidget
                    type={InlineAlertType.Warning}
                    testId='scim-external-id-disable-warning'
                    style={{ marginTop: '16px', marginBottom: '8px', maxWidth: '560px' }}
                >
                    {Messages.scimExternalIdDisableWarning()}
                </CompactInlineAlertWidget>
            }
            {hasChanges &&
                <div style={{ marginTop: '16px' }}>
                    <ButtonWidget
                        buttonType={ButtonType.Outline}
                        testId='scim-external-id-cancel'
                        onClick={handleCancel}
                        disabled={isSaving}
                        style={{ marginRight: '16px' }}
                    >
                        {Messages.cancel()}
                    </ButtonWidget>
                    <ButtonWidget
                        buttonType={ButtonType.Primary}
                        testId='scim-external-id-save'
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {Messages.saveChanges()}
                    </ButtonWidget>
                </div>
            }
        </div>
    );
}
